import { FORM_TYPE_CREATE_RERANKER_FLOW } from 'js/constants/formTypes';
import { useCallback } from 'react';
import usePaging from 'new/state/paging/repository/usePaging';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { addRerankerDataset } from 'js/actions/derivedDataSets';
import { createCodeContainer as addCodeContainer } from 'js/actions/transform';
import { tryCatchAsync } from 'new/utils/TryCatch';

type Response = {
  data: any | null;
  error: string | null;
};

type UseRerankerResponse = {
  isEditMode: boolean;
  startForm: () => void;
  endForm: () => void;
  getMetaInfoById: ReturnType<typeof usePaging>['getMetaInfoById'];
  getSourceConfigFields: ReturnType<typeof usePaging>['getSourceConfigFields'];
  connectionCategory: string | undefined;
  getAuthFields: ReturnType<typeof usePaging>['getAuthFields'];
  createSink: (sinkInfo: any) => Promise<any | undefined>;
  updateSink: (sinkInfo: any) => Promise<any | undefined>;
  onSelectConnection: ReturnType<typeof useFlowsSetup>['onSelectConnection'];
  effectiveConnectionType: ReturnType<typeof useFlowsSetup>['effectiveConnectionType'];
  updateFormData: ReturnType<typeof useFlowsSetup>['updateFormData'];
  resetFormData: ReturnType<typeof useFlowsSetup>['resetFormData'];
  createRerankerNode: (payload: any) => Promise<Response>;
  createCodeContainer: (payload: any) => Promise<Response>;
} & ReturnType<typeof useFlowsSetup>['data'];

export default function useReranker(): UseRerankerResponse {
  const dispatch = useAppDispatch();
  const { getSourceConfigFields, getMetaInfoById, getAuthFields } = usePaging();
  const { createSink: createSinkFromForm, updateSink: updateSinkFromForm } = useFormsAdapter(
    FORM_TYPE_CREATE_RERANKER_FLOW,
  );

  const {
    onSelectConnection,
    data,
    effectiveConnectionType,
    updateFormData,
    resetFormData,
    end,
    start,
  } = useFlowsSetup(FORM_TYPE_CREATE_RERANKER_FLOW, 'reranker');

  const { selectedConnection, credentialId, connectionType } = data;

  const { connectionCategory } = getMetaInfoById(selectedConnection) || {};

  const createSink = useCallback(
    async (sinkInfo) => {
      const response: any = await createSinkFromForm({
        formType: FORM_TYPE_CREATE_RERANKER_FLOW,
        credentialId,
        sinkType: selectedConnection,
        sinkInfo,
      });
      return response?.payload?.formData?.sink;
    },
    [createSinkFromForm, credentialId, selectedConnection, connectionCategory],
  );

  const updateSink = useCallback(
    async (sinkInfo) => {
      const response: any = await updateSinkFromForm({
        sinkInfo,
      });
      return response?.payload?.formData?.sink;
    },
    [updateSinkFromForm, credentialId, selectedConnection, connectionCategory],
  );

  const createRerankerNode = async (payload: any): Promise<Response> => {
    const { data: response, error } = await tryCatchAsync(dispatch(addRerankerDataset(payload)));

    if (error) {
      console.error(error);
      return {
        data: null,
        error: error.message,
      };
    }

    const addedDataSet = Object.values(response.entities.dataSets)[0];

    return {
      data: addedDataSet,
      error: null,
    };
  };

  const createCodeContainer = async (payload: any): Promise<Response> => {
    const { data: response, error } = await tryCatchAsync(dispatch(addCodeContainer(payload)));

    if (error) {
      console.error(error);
      return {
        data: null,
        error: error.message,
      };
    }

    const transform = Object.values(response.entities.transforms)[0];

    return {
      error: null,
      data: transform,
    };
  };

  return {
    ...data,
    isEditMode: !!(connectionType === 'SINK' && data.sink?.id),
    startForm: start,
    endForm: end,
    getMetaInfoById,
    getSourceConfigFields,
    connectionCategory,
    getAuthFields,
    createSink,
    updateSink,
    onSelectConnection: onSelectConnection('reranker'),
    effectiveConnectionType,
    updateFormData,
    resetFormData,
    createRerankerNode,
    createCodeContainer,
  };
}
